import {
  type FeatureDefinitions,
  GrowthBook,
  GrowthBookProvider as GBProvider,
} from '@growthbook/growthbook-react'
import { toBoolean } from 'fitify-utils/src/properties'
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import { logEvent } from './analytics'
import { getNavigatorLocale } from './language'
import { getItem, setItem } from './storage'

const GB_ID_KEY = '@fitify/growthBookId'
const GB_FORCED_VARIATIONS_KEY = '@fitify/growthBookForcedVariations'

const featuresUpdatedListeners = new Set<() => void>()

export const subscribeToGrowthBookFeaturesUpdated = (
  listener: () => void
): (() => void) => {
  featuresUpdatedListeners.add(listener)
  return () => {
    featuresUpdatedListeners.delete(listener)
  }
}

const emitGrowthBookFeaturesUpdated = () => {
  featuresUpdatedListeners.forEach((listener) => listener())
}

export const growthBook = new GrowthBook({
  apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
  clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
  // Enable easier debugging of feature flags during development
  enableDevMode: toBoolean(process.env.NEXT_PUBLIC_GROWTHBOOK_DEV_MODE_ENABLED),
  trackingCallback: (experiment, result) => {
    const growthBookId = getItem('local', GB_ID_KEY)
    logEvent('gb_experiment_impression', {
      experiment_id: experiment.key,
      variation_id: result.key,
      growthbook_id: growthBookId,
    })
    logEvent('gb_experiment_impression_immediate', {
      experiment_id: experiment.key,
      variation_id: result.key,
      growthbook_id: growthBookId,
    })
  },
})

// Patch setRenderer so our subscribers always fire alongside GBProvider's callback.
// GBProvider overwrites the constructor `renderer` via setRenderer — this ensures our
// subscribers are still notified on every feature change regardless.
const originalSetRenderer = growthBook.setRenderer.bind(growthBook)
growthBook.setRenderer = (renderer: () => void) => {
  originalSetRenderer(() => {
    renderer()
    emitGrowthBookFeaturesUpdated()
  })
}

export const parseGrowthBookForcedVariations = (
  searchParams: URLSearchParams
): Record<string, number> => {
  const urlParam = searchParams.get('gbexp')

  if (urlParam) {
    setItem('session', GB_FORCED_VARIATIONS_KEY, urlParam)
  }

  const expParam = urlParam ?? getItem('session', GB_FORCED_VARIATIONS_KEY)

  if (!expParam) {
    return {}
  }

  const forcedVariations: Record<string, number> = {}
  const entries = expParam.split(',')
  for (const entry of entries) {
    const [key, variant] = entry.split(':')
    if (key && variant) {
      const variantIndex = Number(variant)
      if (Number.isInteger(variantIndex) && variantIndex >= 0) {
        forcedVariations[key] = variantIndex
      }
    }
  }
  return forcedVariations
}

const overrideExperimentFromUrlParams = async () => {
  const params = new URLSearchParams(window.location.search)
  const forcedVariations = parseGrowthBookForcedVariations(params)

  if (Object.keys(forcedVariations).length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `[FITIFY] Forced GB experiments: ${JSON.stringify(forcedVariations)}`
    )
    await growthBook.setForcedVariations(forcedVariations)
  }
}

const initGrowthBook = async (defaultFeatures: FeatureDefinitions) => {
  if (toBoolean(process.env.NEXT_PUBLIC_GROWTHBOOK_DEV_MODE_ENABLED)) {
    await overrideExperimentFromUrlParams()
  }
  await growthBook.init({
    timeout: 2000,
    payload: toBoolean(
      process.env.NEXT_PUBLIC_GROWTHBOOK_DEFAULT_FEATURE_FLAGS_ENABLED
    )
      ? {
          features: defaultFeatures,
        }
      : undefined,
  })

  let growthBookId = getItem('local', GB_ID_KEY)
  if (!growthBookId) {
    growthBookId = uuidv4()
    setItem('local', GB_ID_KEY, growthBookId)
  }

  await growthBook.setAttributes({
    id: growthBookId,
    language: getNavigatorLocale(),
  })
}

const GrowthBookDefaultsContext = createContext<FeatureDefinitions>({})

export const useGrowthBookDefaultsContext = <
  T extends Record<string, { defaultValue: unknown }>,
>(): T => {
  const context = useContext(GrowthBookDefaultsContext)
  if (!context) {
    throw new Error(
      'useGrowthBookDefaultsContext must be used within a GrowthBookDefaultsContext.Provider'
    )
  }
  return context as T
}

export const GrowthBookProvider = ({
  children,
  defaultFeatures,
}: PropsWithChildren<{
  defaultFeatures: FeatureDefinitions
}>) => {
  useEffect(() => {
    void initGrowthBook(defaultFeatures)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GBProvider growthbook={growthBook}>
      <GrowthBookDefaultsContext.Provider value={defaultFeatures}>
        {children}
      </GrowthBookDefaultsContext.Provider>
    </GBProvider>
  )
}
